import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  PlayCircle,
  Wallet,
} from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { TitleContext } from "../contexts/TitleContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { toast, ToastContainer } from "react-toastify";
import Snippet from "./Code/Snippet";

interface PlanStatus {
  credits?: number;
  [k: string]: any;
}

const formatBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const NEXT_STEPS = [
  { title: "Crie uma chave de API", path: "/chaves-de-api", titleKey: "Chaves de API" },
  { title: "Faça uma recarga", path: "/recarga", titleKey: "Recarga" },
  { title: "Teste no Playground", path: "/playground", titleKey: "Playground" },
];

const RESOURCES = [
  { title: "Documentação", description: "Guia completo da API", href: "https://docs.maritaca.ai" },
  { title: "Modelos", description: "Catálogo e capacidades", href: null, path: "/modelos", titleKey: "Modelos" },
  { title: "Preços", description: "Tabela por milhão de tokens", href: null, path: "/precos", titleKey: "Preços" },
  { title: "Status da plataforma", description: "Disponibilidade dos serviços", href: "https://status.maritaca.ai" },
];

export default function Info() {
  const navigate = useNavigate();
  const { setTitle } = useContext(TitleContext);
  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [plan, setPlan] = useState<PlanStatus | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const firstName = (user?.given_name as string) || user?.name?.split(" ")[0] || "";

  useEffect(() => {
    const get = async () => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/billing/status`, {
          method: "GET",
          headers: { Authorization: `Auth ${token}` },
        });
        if (res.ok) setPlan(await res.json());
        else {
          reportApiError(res, { feature: "info.fetch", method: "GET" });
          setPlan({ credits: 0 });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "info.fetch" });
        toast.error("Erro ao carregar saldo.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    get();
  }, [getAccessTokenSilently]);

  const go = (titleKey: string, path: string) => {
    setTitle(titleKey);
    navigate(path);
  };

  return (
    <div className="space-y-5">
            {/* Top row: balance + quick start */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
                Saldo disponível
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <Skeleton className="h-9 w-32" />
            ) : (
              <p className="font-mono text-[28px] font-semibold tracking-tight text-foreground">
                {formatBRL(plan?.credits ?? 0)}
              </p>
            )}
            <p className="text-[13px] text-muted-foreground">
              Os créditos não expiram. Recarregue para continuar usando a API.
            </p>
            <Button onClick={() => go("Recarga", "/recarga")} size="sm" className="w-full sm:w-auto">
              Adicionar créditos
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-[15px]">Comece a usar a API</CardTitle>
            <CardDescription>Faça sua primeira chamada em segundos.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Snippet />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => go("Chaves de API", "/chaves-de-api")} size="sm" className="gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Criar chave de API
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => window.open("https://docs.maritaca.ai", "_blank", "noopener,noreferrer")}
              >
                <BookOpen className="h-3.5 w-3.5" />
                Documentação
                <ExternalLink className="h-3 w-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => go("Playground", "/playground")}
              >
                <PlayCircle className="h-3.5 w-3.5" />
                Playground
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: next steps + resources */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-[15px]">Próximos passos</CardTitle>
            <CardDescription>Configure sua conta para começar</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {NEXT_STEPS.map((s, i) => (
                <li key={s.path}>
                  <button
                    onClick={() => go(s.titleKey, s.path)}
                    className="group flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-muted/40"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-background text-[11px] font-medium text-muted-foreground">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-[14px] font-medium text-foreground">{s.title}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-[15px]">Recursos para devs</CardTitle>
            <CardDescription>Documentação e referências</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {RESOURCES.map((r) => {
                const isExternal = !!r.href;
                const onClick = () => {
                  if (isExternal) window.open(r.href!, "_blank", "noopener,noreferrer");
                  else if (r.path && r.titleKey) go(r.titleKey, r.path);
                };
                return (
                  <li key={r.title}>
                    <button
                      onClick={onClick}
                      className="group flex w-full items-start gap-3 px-5 py-3 text-left transition-colors hover:bg-muted/40"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-[14px] font-medium text-foreground">{r.title}</p>
                        <p className="text-[12px] text-muted-foreground">{r.description}</p>
                      </div>
                      {isExternal ? (
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-foreground" />
                      ) : (
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </div>

      <ToastContainer />
    </div>
  );
}